import { useState, useEffect } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { getFirebaseAuthAsync, isFirebaseInitialized } from '@watchlist/shared';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let isCancelled = false;

    const setupAuthListener = async () => {
      // Return early if Firebase isn't ready - will retry on re-render
      if (!isFirebaseInitialized()) {
        console.log('useAuth: Firebase not yet initialized, will retry');
        setLoading(false);
        return;
      }

      try {
        const auth = await getFirebaseAuthAsync();
        if (isCancelled) return;

        unsubscribe = onAuthStateChanged(auth, (user) => {
          if (!isCancelled) {
            setUser(user);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('useAuth: Failed to set up auth listener:', error);
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    setupAuthListener();

    return () => {
      isCancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const auth = await getFirebaseAuthAsync();
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error: any) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const auth = await getFirebaseAuthAsync();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (error: any) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const auth = await getFirebaseAuthAsync();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (error: any) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (isFirebaseInitialized()) {
        const { nativeFirebase } = await import('../services/nativeFirebaseService');
        await nativeFirebase.signOut();
      } else {
        const auth = await getFirebaseAuthAsync();
        await signOut(auth);
      }
    } catch (error: any) {
      setError(error.message);
      throw error;
    }
  };

  return {
    user,
    loading,
    error,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    logout
  };
};
